import { rd } from '@passionware/monads';
import { useSimpleStore } from '@passionware/simple-store';
import { useMemo } from 'react';
import type { DelegatedCoreField } from 'v5/core/entity-schema/resource/resolveResourceSchema';
import { Skeleton } from 'v5/design-system/Skeleton';
import { renderError } from 'v5/patterns/feedback/ErrorAlert';
import {
  ExclusiveEditProvider,
  useCreateExclusiveEdit
} from 'v5/patterns/forms/exclusive-edit/ExclusiveEditContext';
import { UnitForm } from 'v5/patterns/forms/schema-form/UnitForm';
import { useDiscardChangesPrompt } from 'v5/patterns/forms/unsaved-changes/useDiscardChangesPrompt';
import { useRegisterDirtyStateIfProtected } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { renderResourceUnitIcon } from './resourceUnitIcon';
import {
  ResourceFormServices,
  useResourceFormModel
} from './useResourceFormModel';

export interface ResourceGroupsEditorProps extends ResourceFormServices {
  resourceId: number;
  readOnly: boolean;
}

const PRESENTED_BY_HOST: ReadonlyArray<DelegatedCoreField> = [];

/**
 * The resource's groups as one v6 unit, for the resource pane header (the
 * details form leaves groups to it). Renders nothing when the resource type's
 * fields don't include groups.
 */
export function ResourceGroupsEditor({
  resourceId,
  readOnly,
  services
}: ResourceGroupsEditorProps) {
  const modelInput = useMemo(
    () => ({
      mode: 'edit' as const,
      resourceId,
      forceReadOnly: readOnly,
      presentedByHost: PRESENTED_BY_HOST
    }),
    [resourceId, readOnly]
  );
  const model = useResourceFormModel(modelInput, services);
  const discardPrompt = useDiscardChangesPrompt();
  const exclusiveEdit = useCreateExclusiveEdit(discardPrompt.confirmDiscard);
  useRegisterDirtyStateIfProtected(
    useSimpleStore(exclusiveEdit.hasUnsavedDraft)
  );

  return (
    <ExclusiveEditProvider value={exclusiveEdit}>
      {rd
        .journey(model)
        .wait(<Skeleton className="h-8 w-full" />)
        .catch(renderError)
        .map(model => {
          if (model.mode !== 'edit') return null;
          const unit = model.schema.sections
            .find(section => section.id === 'global')
            ?.units.find(
              unit =>
                unit.type === 'fields' &&
                unit.fields.some(field => field.kind === 'groups')
            );
          if (unit === undefined || unit.type !== 'fields') return null;
          const binding = model.bindings[unit.id];
          if (binding?.type !== 'fields') {
            throw new Error(`Missing fields binding for unit ${unit.id}`);
          }
          return (
            <UnitForm
              // Another resource starts outside edit mode.
              key={resourceId}
              unit={unit}
              values={binding.values}
              onSave={binding.save}
              clear={binding.clear}
              overriddenIn={binding.overriddenIn}
              kit={model.kit}
              variant="plain"
              size="sm"
              icon={renderResourceUnitIcon(unit)}
            />
          );
        })}
      {discardPrompt.dialog}
    </ExclusiveEditProvider>
  );
}
